import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { NetworkStatus } from '@cbridge-utils/network';
import { ClientLpAction } from './clientLp.action';
import { clientLpListInitialState } from './clientLp.constant';
import { IClientLpStore } from './clientLp.interface';

export const clientLpReducer = reducerWithInitialState(clientLpListInitialState)
  .case(ClientLpAction.loadList.started, (state) => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Started,
    };
  })
  .case(ClientLpAction.loadList.done, (state, { result }): IClientLpStore => {
    const dataRecord = Object.fromEntries(result.map((lp) => [lp.lpId, lp]));
    return {
      ...state,
      data: dataRecord,
      listNetworkStatus: NetworkStatus.Done,
    };
  })
  .case(ClientLpAction.loadList.failed, (state) => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Failed,
    };
  })
  .case(ClientLpAction.load.started, (state, { lpId }): IClientLpStore => {
    const { data } = state;
    const lpData = data[lpId];

    return {
      ...state,
      data: {
        ...data,
        [lpId]: {
          ...lpData,
          networkStatus: NetworkStatus.Started,
        },
      },
    };
  })
  .case(ClientLpAction.load.done, (state, payload): IClientLpStore => {
    const { data } = state;
    const { params: { lpId }, result } = payload;
    const lpData = data[lpId];

    return {
      ...state,
      data: {
        ...data,
        [lpId]: {
          ...lpData,
          ...result,
          // An LP without a trading connection must not gain an empty `trading` object.
          ...(result.trading || lpData?.trading ? { trading: { ...lpData?.trading, ...result.trading } } : {}),
          pricing: {
            ...lpData?.pricing,
            ...result.pricing,
          },
          networkStatus: NetworkStatus.Done,
        },
      },
    };
  })
  .case(ClientLpAction.load.failed, (state, { params }): IClientLpStore => {
    const { data } = state;
    const { lpId } = params;
    const lpData = data[lpId];

    return {
      ...state,
      data: {
        ...data,
        [lpId]: {
          ...lpData,
          networkStatus: NetworkStatus.Failed,
        },
      },
    };
  })
  .case(ClientLpAction.processing.started, (state): IClientLpStore => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Started,
    };
  })
  .case(ClientLpAction.processing.done, (state): IClientLpStore => {
    return {
      ...state,
      listNetworkStatus: NetworkStatus.Done,
    };
  })
  .case(ClientLpAction.update.done, (state, { params }): IClientLpStore => {
    const { lpId, scheduleId, ...fieldsToUpdate } = params;
    const filteredFields = Object.entries(fieldsToUpdate)
      .filter(([, value]) => value !== undefined)
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    const currentLp = state.data[lpId];
    // Keep scheduleId stable until loadList refreshes the full schedule (avoids blink).
    const schedule = scheduleId
      ? { ...currentLp?.schedule, scheduleId }
      : undefined;
    return {
      ...state,
      data: {
        ...state.data,
        [lpId]: {
          ...currentLp,
          ...filteredFields,
          schedule,
        },
      },
    };
  })
  .case(ClientLpAction.loadAdapterSchema.done, (state, { params, result }): IClientLpStore => ({
    ...state,
    adapterSchemas: {
      ...state.adapterSchemas,
      [params.adapterName]: result,
    },
  }))
  .case(ClientLpAction.loadAdapterSchema.failed, (state, { params }): IClientLpStore => ({
    ...state,
    adapterSchemas: {
      ...state.adapterSchemas,
      [params.adapterName]: null,
    },
  }))
  .case(ClientLpAction.destroy, () => ({
    ...clientLpListInitialState,
  }));
